import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const parsePrice = (val) => {
  if (typeof val === "number") return Number.isFinite(val) ? val : undefined;
  if (typeof val === "string") {
    const n = Number(val.replace(/[^\d.]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }
  return undefined;
};

function EditStatusModal({
  isModalOpen,
  setIsModalOpen,
  statusOptions = [],
  reasons = {},
  listingPrice,
  loading,
  onSubmit
}) {
  const [status, setStatus] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [amount, setAmount] = React.useState("");

  const reasonOptions = reasons[status] || [];
  const listingPriceNum = parsePrice(listingPrice);
  const amountNum = amount === "" ? undefined : Number(amount);
  const exceedsListing =
    status === "SCRAPPED" && listingPriceNum !== undefined && amountNum !== undefined && amountNum > listingPriceNum;
  const amountError = exceedsListing ? `Scrap amount cannot exceed listing price ($${listingPriceNum})` : "";

  const handleClose = () => {
    setIsModalOpen(false);
    setStatus("");
    setReason("");
    setComment("");
    setAmount("");
  };

  const handleSubmit = () => {
    onSubmit({
      status,
      reason: reason || undefined,
      comment: comment || undefined,
      amount: amount ? Number(amount) : undefined
    });
  };

  if (!isModalOpen) return null;

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      PaperProps={{ sx: { maxWidth: "600px", width: "100%", borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Typography fontWeight={700} fontSize="2rem">
          Edit Status
        </Typography>
        <Typography color="#5B5B5B" fontSize="1.4rem" mt="0.4rem">
          Update the status of this vehicle
        </Typography>
        <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8, color: "#000" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "2.4rem !important" }}>
        <Typography fontWeight={600} mb="0.8rem">
          Status *
        </Typography>
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setReason("");
          }}
          displayEmpty
          fullWidth
          sx={{ bgcolor: "#F6F6F6", borderRadius: "8px", mb: "2rem" }}
          renderValue={(val) => {
            if (!val) return <Typography color="#9E9E9E">Select status</Typography>;
            return capitalize(val);
          }}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {capitalize(opt)}
            </MenuItem>
          ))}
        </Select>

        {status && reasonOptions.length > 0 && (
          <>
            <Typography fontWeight={600} mb="0.8rem">
              Reason
            </Typography>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              displayEmpty
              fullWidth
              sx={{ bgcolor: "#F6F6F6", borderRadius: "8px", mb: "2rem" }}
              renderValue={(val) => {
                if (!val) return <Typography color="#9E9E9E">Select reason</Typography>;
                return val;
              }}
            >
              {reasonOptions.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </>
        )}

        {status && (
          <>
            <Typography fontWeight={600} mb="0.8rem">
              Comment
            </Typography>
            <TextField
              placeholder="Add a comment (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              sx={{ bgcolor: "#F6F6F6", borderRadius: "8px", mb: "2rem" }}
            />
          </>
        )}

        {(status === "SCRAPPED" || status === "LOST") && (
          <Box>
            <Typography fontWeight={600} mb="0.8rem">
              Amount
            </Typography>
            <TextField
              placeholder="Enter amount (optional)"
              value={amount}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*\.?\d*$/.test(val)) setAmount(val);
              }}
              fullWidth
              error={Boolean(amountError)}
              helperText={amountError || undefined}
              sx={{ bgcolor: "#F6F6F6", borderRadius: "8px" }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", p: "1.6rem 2.4rem" }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            fontWeight: 600,
            fontSize: "1.6rem",
            borderColor: "#C8C8C8",
            color: "#000",
            borderRadius: "8px",
            px: "2.4rem"
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          disabled={!status || exceedsListing}
          onClick={handleSubmit}
          sx={{
            fontWeight: 600,
            fontSize: "1.6rem",
            bgcolor: !status || exceedsListing ? "#9E9E9E" : "#1A1A1A",
            color: "#fff",
            borderRadius: "8px",
            px: "2.4rem",
            "&:hover": { bgcolor: "#333" },
            "&.Mui-disabled": { bgcolor: "#9E9E9E", color: "#fff" }
          }}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditStatusModal;
